/**
 * OnboardingRole Page — v2 Redesign
 *
 * One-time onboarding: shown ONCE after first sign-up.
 * After completion, onboardingCompleted=true is persisted
 * and this page never shows again (enforced by OnboardingGuard + ProtectedRoute).
 *
 * Features:
 *   - Intent-driven role cards with monetization-aware microcopy
 *   - Academic level picker for students
 *   - Context-aware preselection from referrer
 *   - Role-specific post-onboarding destinations
 *   - Full EN/AR + RTL support
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  GraduationCap,
  Store,
  ShoppingBag,
  Briefcase,
  Users,
  Check,
  ArrowRight,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import './OnboardingRole.css';
import { getApiBase } from '../api/httpClient';

// ══════════════════════════════════════════
// ROLE CONFIG — maps UI labels → backend keys
// ══════════════════════════════════════════

const ACCOUNT_TYPES = [
  {
    key: 'seller',
    icon: Store,
    labelKey: 'onborole.sellerLabel',
    descKey: 'onborole.sellerDesc',
    color: '#F97316',
    bg: '#FFF7ED',
  },
  {
    key: 'buyer',
    icon: ShoppingBag,
    labelKey: 'onborole.buyerLabel',
    descKey: 'onborole.buyerDesc',
    color: '#3B82F6',
    bg: '#EFF6FF',
  },
  {
    key: 'student',
    icon: GraduationCap,
    labelKey: 'onborole.studentLabel',
    descKey: 'onborole.studentDesc',
    color: '#8B5CF6',
    bg: '#F5F3FF',
  },
  {
    key: 'service',
    icon: Briefcase,
    labelKey: 'onborole.serviceLabel',
    descKey: 'onborole.serviceDesc',
    color: '#10B981',
    bg: '#ECFDF5',
  },
  {
    key: 'professional',
    icon: Users,
    labelKey: 'onborole.proLabel',
    descKey: 'onborole.proDesc',
    color: '#6366F1',
    bg: '#EEF2FF',
  },
  {
    key: 'general',
    icon: Users,
    labelKey: 'onborole.generalLabel',
    descKey: 'onborole.generalDesc',
    color: '#6B7280',
    bg: '#F3F4F6',
  },
];

const ACADEMIC_LEVELS = [
  { key: 'secondary', label_en: 'Secondary', label_ar: 'ثانوي' },
  { key: 'university', label_en: 'University', label_ar: 'جامعي' },
  { key: 'postgrad', label_en: 'Postgraduate', label_ar: 'دراسات عليا' },
  { key: 'vocational', label_en: 'Vocational', label_ar: 'مهني/فني' },
  { key: 'other', label_en: 'Other', label_ar: 'أخرى' },
];

// Role → destination after onboarding
const DESTINATIONS = {
  seller: '/add-ad',
  buyer: '/',
  student: '/student-hub',
  service: '/add-ad',
  professional: '/',
  general: '/',
};

// Context-aware preselection from referrer path
function guessRoleFromContext(from) {
  if (!from) return null;
  const path = typeof from === 'string' ? from : from.pathname || '';
  if (path.includes('add-ad') || path.includes('create-ad') || path.includes('post')) return 'seller';
  if (path.includes('student') || path.includes('book') || path.includes('cv')) return 'student';
  if (path.includes('shop') || path.includes('browse')) return 'buyer';
  return null;
}

export default function OnboardingRole() {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isRTL = i18n.language?.startsWith('ar');

  const from = location.state?.from;
  const contextGuess = useMemo(() => guessRoleFromContext(from), [from]);

  const [accountType, setAccountType] = useState(contextGuess || 'seller');
  const [preferredLevel, setPreferredLevel] = useState('university');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Once-only "seen" marker — the intro onboarding must appear exactly once
  // per account. Fired on first display; if the user abandons this page
  // (force-close / back) they won't be re-prompted on the next sign-in and
  // can set their role later from My Account. Idempotent server-side.
  const seenSentRef = useRef(false);
  useEffect(() => {
    if (!token || seenSentRef.current) return;
    seenSentRef.current = true;
    fetch(`${getApiBase()}/api/me/onboarding-seen`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => { /* best-effort */ });
  }, [token]);

  const handleConfirm = async () => {
    if (!token) {
      navigate('/welcome', { replace: true });
      return;
    }
    if (saving) return;

    setSaving(true);
    setError('');

    try {
      // iter285 — use getApiBase() so live custom domains hit
      // same-origin /api/onboarding. Previously a stale build-time
      // REACT_APP_BACKEND_URL could leak the call to a different host
      // → cross-origin → fetch fails → onboarding NEVER persists →
      // user sees the picker forever (the loop reported on live).
      const response = await fetch(`${getApiBase()}/api/onboarding`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          account_type: accountType,
          preferred_level: accountType === 'student' ? preferredLevel : null,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to save');
      }

      // Mark locally
      localStorage.setItem('onboarding_version_completed', 'v2');
      localStorage.setItem('wassla_profile_type', accountType);
      localStorage.setItem('hasCompletedProfileSelection', 'true');
      if (accountType === 'student' && preferredLevel) {
        localStorage.setItem('studentHub.level', preferredLevel);
      }

      // Role-specific destination
      const returnPath =
        typeof from === 'object' && from?.pathname ? from.pathname : typeof from === 'string' ? from : null;
      let dest = DESTINATIONS[accountType] || '/';
      if (returnPath && returnPath !== '/' && returnPath !== '/onboarding') {
        dest = returnPath;
      }

      // Hard redirect so AuthContext re-fetches user with onboardingCompleted=true.
      // HashRouter: the destination must live in the hash, not the pathname.
      window.location.href = `${window.location.origin}/#${dest}`;
      window.location.reload();
    } catch (err) {
      console.error('Onboarding error:', err);
      setError(err.message || t('onborole.anErrorOccurredPleaseTry'));
    } finally {
      setSaving(false);
    }
  };

  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  return (
    <div className="ob-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid="onboarding-page">
      <div className="ob-container">
        {/* Header */}
        <div className="ob-header">
          <h1 className="ob-title">{t('onborole.welcomeToWassla')}</h1>
          <p className="ob-subtitle">{t('onborole.selectYourProfileTypeTo')}</p>
        </div>

        {/* Section label */}
        <p className="ob-section-label">{t('onborole.iAmA')}</p>

        {/* Cards */}
        <div className="ob-grid" data-testid="onboarding-grid">
          {ACCOUNT_TYPES.map((type) => {
            const Icon = type.icon;
            const isSelected = accountType === type.key;
            return (
              <button
                key={type.key}
                className={`ob-card${isSelected ? ' ob-card--selected' : ''}`}
                onClick={() => setAccountType(type.key)}
                data-testid={`account-type-${type.key}`}
                style={
                  isSelected
                    ? { borderColor: type.color, '--ob-accent': type.color, '--ob-bg': type.bg }
                    : { '--ob-accent': type.color, '--ob-bg': type.bg }
                }
              >
                <div
                  className="ob-icon"
                  style={{
                    background: isSelected ? type.color : type.bg,
                    color: isSelected ? '#fff' : type.color,
                  }}
                >
                  <Icon size={20} />
                </div>
                <div className="ob-card-text">
                  <span className="ob-label">{t(type.labelKey)}</span>
                  <span className="ob-desc">{t(type.descKey)}</span>
                </div>
                {isSelected && (
                  <div className="ob-check" style={{ background: type.color }}>
                    <Check size={12} strokeWidth={3} color="#fff" />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Academic Level — students only */}
        {accountType === 'student' && (
          <div className="ob-level" data-testid="level-section">
            <label className="ob-level-label">{t('onborole.academicLevel')}</label>
            <Select value={preferredLevel} onValueChange={setPreferredLevel}>
              <SelectTrigger className="ob-level-trigger" data-testid="level-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACADEMIC_LEVELS.map((lv) => (
                  <SelectItem key={lv.key} value={lv.key}>
                    {isRTL ? lv.label_ar : lv.label_en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="ob-error" data-testid="onboarding-error">
            {error}
          </div>
        )}

        {/* CTA */}
        <button
          className="ob-cta"
          onClick={handleConfirm}
          disabled={saving}
          data-testid="onboarding-confirm-btn"
        >
          {saving ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              {t('onborole.saving')}
            </>
          ) : (
            <>
              {t('onborole.continue')}
              <ArrowIcon size={18} />
            </>
          )}
        </button>

        {/* Note */}
        <p className="ob-note">{t('onborole.youCanChangeThisLater')}</p>
      </div>
    </div>
  );
}
